/**
 * Labelled text input with inline validation.
 *
 * The artwork designed no error state (gap G-11); this adds one in the same
 * visual language and announces it to screen readers, which the master
 * prompt's accessibility rules require.
 */

import { memo, forwardRef } from 'react';
import {
  StyleSheet,
  TextInput,
  View,
  type TextInputProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, radius, space, textStyles } from '../../theme';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';

export interface FieldProps extends Omit<TextInputProps, 'style'> {
  label: string;
  error?: string | null;
  hint?: string;
  required?: boolean;
  /** Glyph name rendered inside the input, matching the designs. */
  icon?: string;
  containerStyle?: StyleProp<ViewStyle>;
  testID?: string;
}

export const Field = memo(
  forwardRef<TextInput, FieldProps>(function Field(
    { label, error, hint, required = false, icon, containerStyle, testID, ...inputProps },
    ref,
  ) {
    const inputTestID = testID ?? `field-${label.toLowerCase().replace(/\s+/g, '-')}`;
    return (
      <View style={[styles.container, containerStyle]}>
        <Text variant="label" tone="secondary">
          {label}
          {required ? <Text tone="danger"> *</Text> : null}
        </Text>

        <View style={[styles.inputRow, error ? styles.inputRowError : null]}>
          {icon ? <Glyph name={icon} size={16} /> : null}
          <TextInput
            ref={ref}
            testID={inputTestID}
            style={styles.input}
            placeholderTextColor={colors.text.disabled}
            accessibilityLabel={label}
            accessibilityHint={hint}
            // The error is surfaced to assistive tech, not just drawn in red.
            accessibilityState={{ disabled: inputProps.editable === false }}
            {...inputProps}
          />
        </View>

        {error ? (
          <Text
            variant="caption"
            tone="danger"
            accessibilityLiveRegion="polite"
            testID={`${inputTestID}-error`}
          >
            {error}
          </Text>
        ) : hint ? (
          <Text variant="caption" tone="tertiary">
            {hint}
          </Text>
        ) : null}
      </View>
    );
  }),
);

const styles = StyleSheet.create({
  container: {
    gap: space.xs,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    borderWidth: 1,
    borderColor: colors.border.default,
    borderRadius: radius.md,
    backgroundColor: colors.bg.surface,
    paddingHorizontal: space.md,
    minHeight: 52,
  },
  inputRowError: {
    borderColor: colors.text.danger,
  },
  input: {
    flex: 1,
    paddingVertical: space.md,
    color: colors.text.primary,
    fontSize: textStyles.body.fontSize,
  },
});
